/**
 * mainUser slice: 화면 포커스(메인 화면에 표시할) 참여자 ID 관리
 * wehago-conference의 redux/modules/mainUser 에서 리팩토링
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { bareUserId } from '@/utils/participantUtils';

// re-export for backwards compatibility
export { bareUserId } from '@/utils/participantUtils';

export interface MainUserState {
  // 메인 화면에 고정된 참여자 ID (null이면 자동 선택)
  pinnedUserId: string | null;
  // 핀된 유저의 공유화면 표시 여부 (true=공유화면, false=카메라, null=자동)
  pinnedShowScreenShare: boolean | null;
  // 실제 메인 화면에 표시 중인 참여자 ID (dominant speaker 또는 pinned)
  mainUserId: string | null;
  // 개인 화면 고정 목록 (최대 2개) — v1 pinnedUsers[]
  pinnedUserIds: string[];
  // 마스터가 고정한 화면 목록 (최대 2개) — v1 masterPinnedUsers[]
  masterPinnedUserIds: string[];
}

const initialState: MainUserState = {
  pinnedUserId: null,
  pinnedShowScreenShare: null,
  mainUserId: null,
  pinnedUserIds: [],
  masterPinnedUserIds: [],
};

const mainUserSlice = createSlice({
  name: 'mainUser',
  initialState,
  reducers: {
    setMainUser(state, action: PayloadAction<string | null>) {
      state.mainUserId = action.payload;
    },

    /** 단일 핀 (기존 호환) */
    pinUser(state, action: PayloadAction<string | null | { userId: string | null; showScreenShare?: boolean }>) {
      const payload = action.payload;
      const userId = payload !== null && typeof payload === 'object' ? payload.userId : payload;
      const showScreenShare = payload !== null && typeof payload === 'object' ? (payload.showScreenShare ?? null) : null;
      state.pinnedUserId = userId;
      state.pinnedShowScreenShare = showScreenShare;
      if (userId !== null) {
        state.mainUserId = userId;
      }
    },

    /** 개인 핀 목록 직접 교체 — v1 setPinnedUsers */
    setPinnedUsers(state, action: PayloadAction<string[]>) {
      state.pinnedUserIds = action.payload;
      // 단일 핀도 동기화: 마지막 항목 또는 null (복합키에서 순수 userId 추출)
      const last = action.payload[action.payload.length - 1] ?? null;
      state.pinnedUserId = last ? bareUserId(last) : null;
      state.pinnedShowScreenShare = null;
      if (last) state.mainUserId = bareUserId(last);
    },

    /** 마스터 핀 목록 교체 (브로드캐스트 수신 시) */
    setMasterPinnedUsers(state, action: PayloadAction<string[]>) {
      state.masterPinnedUserIds = action.payload;
      // 마스터 핀이 있으면 개인 핀 해제 (v1 정책)
      if (action.payload.length > 0) {
        state.pinnedUserIds = [];
        state.pinnedUserId = null;
        state.pinnedShowScreenShare = null;
        // 마스터 핀 첫 번째를 mainUser로 (복합키에서 순수 userId 추출)
        state.mainUserId = bareUserId(action.payload[0]);
      }
    },

    /** 유저 퇴장 시 관련 핀 정리 */
    removeUser(state, action: PayloadAction<string>) {
      const id = action.payload;

      // 개인 핀 목록에서 제거
      state.pinnedUserIds = state.pinnedUserIds.filter((uid) => bareUserId(uid) !== id);
      const lastPinned = state.pinnedUserIds[state.pinnedUserIds.length - 1] ?? null;
      state.pinnedUserId = lastPinned ? bareUserId(lastPinned) : null;
      if (!lastPinned || state.pinnedUserId !== id) state.pinnedShowScreenShare = null;

      // 마스터 핀 목록에서 제거
      state.masterPinnedUserIds = state.masterPinnedUserIds.filter((uid) => bareUserId(uid) !== id);

      // 메인 유저 재설정
      if (state.masterPinnedUserIds.length > 0) {
        state.mainUserId = bareUserId(state.masterPinnedUserIds[0]);
      } else if (state.pinnedUserId) {
        state.mainUserId = state.pinnedUserId;
      } else if (state.mainUserId === id) {
        state.mainUserId = null;
      }
    },

    reset() {
      return initialState;
    },
  },
});

export const mainUserActions = mainUserSlice.actions;
export default mainUserSlice.reducer;
